import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import domtoimage from 'dom-to-image-more';
import { 
  CalendarIcon, 
  Printer, 
  ArrowRight,
  ArrowLeft,
  Download,
  FileText,
  Edit,
  Trash2
} from 'lucide-react';

const CustomerStatementPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const statementRef = useRef(null);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  
  const isRTL = language === 'ar' || language === 'ku';
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;
  
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysFromSunday = dayOfWeek === 0 ? 0 : dayOfWeek;
  const sundayStart = new Date(today);
  sundayStart.setDate(today.getDate() - daysFromSunday);
  
  const [startDate, setStartDate] = useState(sundayStart);
  const [endDate, setEndDate] = useState(new Date(sundayStart.getTime() + 5 * 24 * 60 * 60 * 1000));

  const dayNames = {
    ar: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
    de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    tr: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
    ku: ['Yş', 'Dş', 'Sş', 'Çş', 'Pş', 'În', 'Şe']
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return dayNames[language]?.[date.getDay()] || dayNames.en[date.getDay()];
  };

  useEffect(() => {
    fetchStatement();
  }, [customerId, startDate, endDate]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_URL}/api/customers/${customerId}/statement?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeader() }
      );
      setStatement(response.data);
    } catch (error) {
      console.error('Error fetching statement:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!statementRef.current) return;
    
    const content = statementRef.current.innerHTML;
    
    const printHTML = `
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>كشف حساب - ${statement?.customer_name}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { 
            font-family: Arial, Tahoma, sans-serif; 
            margin: 0; 
            padding: 20px;
            direction: ${isRTL ? 'rtl' : 'ltr'};
          }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px; border: 1px solid #ddd; }
          th { background: #f5f5f5; }
          .no-print { display: none !important; }
        </style>
      </head>
      <body>
        ${content}
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  const downloadImage = async () => {
    if (!statementRef.current) return;
    
    try {
      toast.loading(t('creatingImage') || 'جاري إنشاء الصورة...');
      
      const node = statementRef.current;
      
      // Higher quality settings for better image - WhatsApp optimized
      const scale = 4; // Increased scale for higher quality
      const dataUrl = await domtoimage.toPng(node, {
        quality: 1.0,
        bgcolor: '#ffffff',
        width: node.offsetWidth * scale,
        height: node.offsetHeight * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${node.offsetWidth}px`,
          height: `${node.offsetHeight}px`,
          fontSmoothing: 'antialiased',
          webkitFontSmoothing: 'antialiased'
        },
        filter: (node) => {
          // Filter out no-print elements
          return !node.classList?.contains('no-print');
        }
      });
      
      toast.dismiss();
      
      const link = document.createElement('a');
      link.download = `كشف_${statement?.customer_name}_${format(new Date(), 'yyyy-MM-dd')}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success(t('imageDownloaded') || 'تم تحميل الصورة');
    } catch (error) {
      console.error('Error:', error);
      toast.dismiss();
      toast.error(t('copyImageError') || 'خطأ في إنشاء الصورة');
    }
  };

  const formatCurrency = (amount) => amount?.toFixed(2) || '0.00';

  const openEditPayment = (payment) => {
    setEditingPayment({ ...payment });
    setEditPaymentOpen(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment?.id) return;
    try {
      await axios.put(
        `${API_URL}/api/payments/${editingPayment.id}`,
        {
          amount: parseFloat(editingPayment.amount),
          date: editingPayment.date,
          notes: editingPayment.notes || ''
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('paymentUpdated') || 'تم تعديل الدفعة');
      setEditPaymentOpen(false);
      fetchStatement();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error(t('errorOccurred') || 'حدث خطأ');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm(t('deletePaymentConfirm') || 'هل تريد حذف هذه الدفعة؟')) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${paymentId}`, { headers: getAuthHeader() });
      toast.success(t('paymentDeleted') || 'تم حذف الدفعة');
      fetchStatement();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error(t('errorOccurred') || 'حدث خطأ');
    }
  };

  // Calculate totals with prices (using delivered quantities when available)
  const getProductTotals = () => {
    const totals = {};
    const prices = {};
    statement?.orders?.forEach(order => {
      order.items?.forEach(item => {
        // Use delivered_quantity if available, otherwise use quantity
        const actualQty = (item.delivered_quantity !== undefined && item.delivered_quantity !== null) 
          ? item.delivered_quantity 
          : item.quantity;
        totals[item.product_name] = (totals[item.product_name] || 0) + actualQty;
        // Calculate total price for this product based on actual delivered quantity
        const itemTotal = actualQty * (item.price || 0);
        prices[item.product_name] = (prices[item.product_name] || 0) + itemTotal;
      });
    });
    return { totals, prices };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="p-4">
        <Button variant="ghost" onClick={() => navigate('/customers')}>
          <BackArrow className="w-4 h-4 me-2" />
          {t('backToCustomers')}
        </Button>
        <div className="empty-state mt-8">
          <FileText className="empty-state-icon" />
          <h3 className="empty-state-title">{t('dataNotFound')}</h3>
        </div>
      </div>
    );
  }

  const { totals: productTotals, prices: productPrices } = getProductTotals();
  const totalBoxes = Object.values(productTotals).reduce((a, b) => a + b, 0);
  const totalPrice = Object.values(productPrices).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header Controls */}
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/customers')} className="text-stone-600">
            <BackArrow className="w-4 h-4 me-2" />
            {t('backButton')}
          </Button>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-stone-500">{t('from')}:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarIcon className="w-4 h-4 me-2" />
                  {format(startDate, 'MM/dd')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} />
              </PopoverContent>
            </Popover>
            
            <span className="text-stone-500">{t('to')}:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarIcon className="w-4 h-4 me-2" />
                  {format(endDate, 'MM/dd')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} />
              </PopoverContent>
            </Popover>
          </div>
          
          <Button onClick={downloadImage} className="bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 me-2" />
            {t('downloadImage')}
          </Button>
          <Button onClick={handlePrint} variant="outline">
            <Printer className="w-4 h-4 me-2" />
            {t('print')}
          </Button>
        </div>
      </div>

      {/* Statement Content */}
      <div 
        ref={statementRef}
        className="statement-print-container"
        style={{
          fontFamily: 'Arial, Tahoma, sans-serif',
          backgroundColor: '#fff',
          padding: '30px',
          maxWidth: '800px',
          margin: '0 auto',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          padding: '25px',
          borderRadius: '12px',
          marginBottom: '25px',
          textAlign: 'center'
        }}>
          <div style={{fontSize: '28px', fontWeight: 'bold', marginBottom: '5px'}}>
            {user?.company_info?.company_name || 'المخبز'}
          </div>
          {user?.company_info?.phone && (
            <div style={{fontSize: '14px', opacity: 0.9}}>📞 {user.company_info.phone}</div>
          )}
          {user?.company_info?.address && (
            <div style={{fontSize: '14px', opacity: 0.9}}>📍 {user.company_info.address}</div>
          )}
        </div>

        {/* Customer Info Card */}
        <div style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '25px'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
            <div>
              <div style={{fontSize: '12px', color: '#92400e', marginBottom: '3px'}}>{t('customerName')}</div>
              <div style={{fontSize: '22px', fontWeight: 'bold', color: '#78350f'}}>{statement.customer_name}</div>
              {statement.shop_name && (
                <div style={{fontSize: '14px', color: '#a16207'}}>{statement.shop_name}</div>
              )}
            </div>
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '12px', color: '#92400e'}}>{t('statementPeriod')}</div>
              <div style={{fontSize: '14px', fontWeight: 'bold', color: '#78350f'}}>
                {format(startDate, 'yyyy/MM/dd')} - {format(endDate, 'yyyy/MM/dd')}
              </div>
            </div>
          </div>
        </div>

        {/* Previous Balance */}
        {statement.previous_balance !== 0 && (
          <div style={{
            background: statement.previous_balance > 0 ? '#fef2f2' : '#ecfdf5',
            border: `2px solid ${statement.previous_balance > 0 ? '#fca5a5' : '#6ee7b7'}`,
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{fontWeight: 'bold', color: statement.previous_balance > 0 ? '#dc2626' : '#059669'}}>
              {t('previousBalance')}
            </span>
            <span style={{fontSize: '20px', fontWeight: 'bold', color: statement.previous_balance > 0 ? '#dc2626' : '#059669'}}>
              {formatCurrency(statement.previous_balance)} €
            </span>
          </div>
        )}

        {/* Orders Section */}
        {statement.orders?.length > 0 && (
          <div style={{marginBottom: '25px'}}>
            <div style={{
              background: '#1e40af',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '8px 8px 0 0',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              📦 {t('ordersTitle')} ({statement.orders.length})
            </div>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <thead>
                <tr style={{background: '#dbeafe'}}>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #93c5fd'}}>{t('dateColumn')}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #93c5fd'}}>{t('dayColumn')}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #93c5fd'}}>{t('productsColumn')}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #93c5fd'}}>{t('totalColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {statement.orders.map((order, idx) => {
                  // Check if this order has partial delivery
                  const hasPartialDelivery = order.items?.some(item => 
                    item.delivered_quantity !== undefined && 
                    item.delivered_quantity !== null && 
                    item.delivered_quantity < item.quantity
                  );
                  
                  return (
                    <tr key={idx} style={{background: idx % 2 === 0 ? '#f8fafc' : '#fff'}}>
                      <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>{order.date}</td>
                      <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>{getDayName(order.date)}</td>
                      <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>
                        {order.items.map((item, itemIdx) => {
                          const isPartial = item.delivered_quantity !== undefined && 
                                           item.delivered_quantity !== null && 
                                           item.delivered_quantity < item.quantity;
                          return (
                            <span key={itemIdx}>
                              {itemIdx > 0 && ' - '}
                              {isPartial ? (
                                <span style={{color: '#dc2626', fontWeight: 'bold'}}>
                                  {item.product_name}(<s style={{color: '#9ca3af'}}>{item.quantity}</s>→{item.delivered_quantity})
                                </span>
                              ) : (
                                <span>{item.product_name}({item.quantity})</span>
                              )}
                            </span>
                          );
                        })}
                        {hasPartialDelivery && (
                          <span style={{
                            fontSize: '10px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            marginRight: '8px'
                          }}>
                            ⚠️ {t('partialDelivery') || 'توصيل جزئي'}
                          </span>
                        )}
                      </td>
                      <td style={{
                        padding: '10px', 
                        borderBottom: '1px solid #e5e7eb', 
                        fontWeight: 'bold', 
                        color: hasPartialDelivery ? '#dc2626' : '#1e40af'
                      }}>
                        {formatCurrency(order.total_with_tax)} €
                        {hasPartialDelivery && order.original_total && order.original_total !== order.total_with_tax && (
                          <div style={{fontSize: '11px', color: '#9ca3af', textDecoration: 'line-through'}}>
                            {formatCurrency(order.original_total)} €
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Products Summary */}
        {Object.keys(productTotals).length > 0 && (
          <div style={{
            background: '#fef3c7',
            border: '2px solid #fbbf24',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '25px'
          }}>
            <div style={{fontWeight: 'bold', color: '#92400e', marginBottom: '15px', fontSize: '16px'}}>
              📊 {t('totalBoxes')}
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center'}}>
              {Object.entries(productTotals).map(([name, qty]) => (
                <div key={name} style={{
                  background: 'white',
                  padding: '15px 25px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '2px solid #fcd34d',
                  minWidth: '130px'
                }}>
                  <div style={{fontSize: '28px', fontWeight: 'bold', color: '#b45309'}}>{qty}</div>
                  <div style={{fontSize: '14px', color: '#78350f', marginTop: '5px'}}>{name}</div>
                  <div style={{fontSize: '16px', fontWeight: 'bold', color: '#059669', marginTop: '8px', borderTop: '1px solid #fcd34d', paddingTop: '8px'}}>
                    {formatCurrency(productPrices[name])} €
                  </div>
                </div>
              ))}
              <div style={{
                background: '#059669',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '10px',
                textAlign: 'center',
                minWidth: '130px'
              }}>
                <div style={{fontSize: '28px', fontWeight: 'bold'}}>{totalBoxes}</div>
                <div style={{fontSize: '14px', marginTop: '5px'}}>{t('total')}</div>
                <div style={{fontSize: '16px', fontWeight: 'bold', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '8px'}}>
                  {formatCurrency(totalPrice)} €
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Section */}
        {statement.payments?.length > 0 && (
          <div style={{marginBottom: '25px'}}>
            <div style={{
              background: '#059669',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '8px 8px 0 0',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              💰 {t('paymentsTitle')} ({statement.payments.length})
            </div>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <thead>
                <tr style={{background: '#d1fae5'}}>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #6ee7b7'}}>{t('dateColumn')}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #6ee7b7'}}>{t('amountColumn')}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #6ee7b7'}}>{t('paymentType') || 'نوع الدفع'}</th>
                  <th style={{padding: '10px', textAlign: 'right', borderBottom: '2px solid #6ee7b7'}}>{t('notesColumn')}</th>
                  <th className="no-print" style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #6ee7b7', width: '100px'}}>{t('actions') || 'إجراءات'}</th>
                </tr>
              </thead>
              <tbody>
                {statement.payments.map((payment, idx) => (
                  <tr key={idx} style={{background: idx % 2 === 0 ? '#f0fdf4' : '#fff'}}>
                    <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>{payment.date}</td>
                    <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#059669'}}>
                      {formatCurrency(payment.amount)} €
                    </td>
                    <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>
                      {payment.payment_type === 'driver_collection' ? (
                        <span style={{background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}}>
                          🚚 استلام سائق
                        </span>
                      ) : payment.payment_type === 'partial' ? (
                        <span style={{background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}}>
                          جزئي
                        </span>
                      ) : (
                        <span style={{background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '4px', fontSize: '12px'}}>
                          كامل
                        </span>
                      )}
                    </td>
                    <td style={{padding: '10px', borderBottom: '1px solid #e5e7eb'}}>{payment.notes || '-'}</td>
                    <td className="no-print" style={{padding: '6px', borderBottom: '1px solid #e5e7eb', textAlign: 'center'}}>
                      <div style={{display: 'flex', gap: '4px', justifyContent: 'center'}}>
                        <button
                          data-testid={`edit-payment-${idx}`}
                          onClick={() => openEditPayment(payment)}
                          style={{padding: '4px 8px', border: '1px solid #3b82f6', borderRadius: '6px', background: '#eff6ff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}
                        >
                          <Edit style={{width: '14px', height: '14px', color: '#3b82f6'}} />
                        </button>
                        <button
                          data-testid={`delete-payment-${idx}`}
                          onClick={() => handleDeletePayment(payment.id)}
                          style={{padding: '4px 8px', border: '1px solid #ef4444', borderRadius: '6px', background: '#fef2f2', cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}
                        >
                          <Trash2 style={{width: '14px', height: '14px', color: '#ef4444'}} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Final Summary */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: 'white',
          borderRadius: '12px',
          padding: '25px',
          marginTop: '20px'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px'}}>
            <div style={{textAlign: 'center', flex: '1', minWidth: '150px'}}>
              <div style={{fontSize: '14px', opacity: 0.8, marginBottom: '5px'}}>{t('totalSalesLabel')}</div>
              <div style={{fontSize: '24px', fontWeight: 'bold'}}>{formatCurrency(statement.total_with_tax)} €</div>
            </div>
            <div style={{textAlign: 'center', flex: '1', minWidth: '150px'}}>
              <div style={{fontSize: '14px', opacity: 0.8, marginBottom: '5px'}}>{t('paidLabel')}</div>
              <div style={{fontSize: '24px', fontWeight: 'bold', color: '#4ade80'}}>{formatCurrency(statement.total_paid)} €</div>
            </div>
            <div style={{
              textAlign: 'center',
              flex: '1',
              minWidth: '150px',
              background: statement.balance > 0 ? '#ef4444' : '#22c55e',
              padding: '15px',
              borderRadius: '10px'
            }}>
              <div style={{fontSize: '14px', marginBottom: '5px'}}>{t('remainingBalance')}</div>
              <div style={{fontSize: '28px', fontWeight: 'bold'}}>{formatCurrency(statement.balance)} €</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          padding: '10px',
          color: '#9ca3af',
          fontSize: '12px',
          borderTop: '1px solid #e5e7eb'
        }}>
          {t('statementCreated')} {format(new Date(), 'yyyy/MM/dd HH:mm')}
        </div>
      </div>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentOpen} onOpenChange={setEditPaymentOpen}>
        <DialogContent className="max-w-md no-print">
          <DialogHeader>
            <DialogTitle>{t('editPayment') || 'تعديل الدفعة'}</DialogTitle>
          </DialogHeader>
          {editingPayment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('amount') || 'المبلغ'} (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingPayment.amount}
                  onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })}
                  data-testid="edit-payment-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('date') || 'التاريخ'}</Label>
                <Input
                  type="date"
                  value={editingPayment.date}
                  onChange={(e) => setEditingPayment({ ...editingPayment, date: e.target.value })}
                  data-testid="edit-payment-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('notes') || 'ملاحظات'}</Label>
                <Input
                  value={editingPayment.notes || ''}
                  onChange={(e) => setEditingPayment({ ...editingPayment, notes: e.target.value })}
                  data-testid="edit-payment-notes"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setEditPaymentOpen(false)}>
                  {t('cancel') || 'إلغاء'}
                </Button>
                <Button
                  data-testid="save-payment-btn"
                  onClick={handleUpdatePayment}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {t('save') || 'حفظ'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerStatementPage;
